/* eslint-disable no-param-reassign */
/* eslint-disable prefer-promise-reject-errors */
import {
  React,
  useState,
  useEffect,
  useContext,
  createContext,
} from '@biletiniz/ui-core';
import { isEqual } from '@biletiniz/shared';
import { tokenRefreshManager } from '@biletiniz/apollo/token-refresh-manager';
import { useSession } from './useSession';
import { useToken } from '../auth';
import { useToast } from '../useToast';
import { useGetUser } from '../get-user';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

function useProvideAuth(apolloClient) {
  const session = useSession();
  // TODO use handleServerError
  // eslint-disable-next-line no-unused-vars
  const [handleServerError, setHandleServerError] = useState();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    getUser,
    error,
    loading: getUserLoading,
    data: userData,
  } = useGetUser(apolloClient);
  const { getToken } = useToken(apolloClient);
  const { setMessage } = useToast();

  const clearSession = () => {
    session.setIsSessionExpired(true);
    session.clearToken();
    setUser(undefined);

    // Redirect to login page
    if (typeof window !== 'undefined') {
      window.location.href = '/giris';
    }
  };

  const handleUpdateError = message => {
    setMessage(message);
    clearSession();
  };

  useEffect(() => {
    if (error) {
      handleUpdateError(error?.message);
    }
  }, [error]);

  useEffect(() => {
    if (userData) {
      if (!isEqual(user, userData)) {
        setUser(userData);
      }
    }
  }, [userData]);

  const storeSession = async token => {
    setIsLoading(true);
    const { accessToken, refreshToken, user: newUser } = token;
    session.setToken({ accessToken, refreshToken });
    setUser(newUser);
    session.setIsSessionExpired(false);
    setIsLoading(false);
  };

  const updateUser = () => {
    if (!(isLoading || getUserLoading)) {
      getUser();
    }
  };

  const refreshToken = (appLoad = true, refreshTokenValue) => {
    return new Promise((resolve, reject) => {
      if (appLoad) setIsLoading(true);

      // Use tokenRefreshManager to coordinate all refresh requests
      tokenRefreshManager
        .startRefresh(() => {
          // Actual refresh logic
          return getToken(refreshTokenValue ?? session.refreshToken)
            .then(({ data }) => {
              const {
                accessToken,
                refreshToken: newRefreshToken,
                user: newUser,
              } = data?.token?.token || {};

              // If no access token, refresh failed
              if (!accessToken) {
                clearSession(); // This will redirect to login
                // TODO Aytan
                throw new Error('Token refresh failed');
              }

              // Update session state
              session.setToken({ accessToken, refreshToken: newRefreshToken });
              setUser(newUser);
              session.setIsSessionExpired(false);

              return accessToken;
            })
            .catch(errors => {
              // Clear session and redirect to login
              clearSession();
              throw errors;
            });
        })
        .then(accessToken => {
          resolve(accessToken);
        })
        .catch(err => {
          setMessage(err.message);
          reject(err);
        })
        .finally(() => {
          if (appLoad) setIsLoading(false);
        });
    });
  };

  // const unauthenticatedCallback = (error, refreshTokenValue) => {
  //   return refreshToken(false, refreshTokenValue).then(({ accessToken }) => {
  //     error.config.headers.Authorization = `Bearer ${accessToken}`;
  //     return axios.request(error.config);
  //   });
  // };

  useEffect(() => {
    if (session.isSessionExpired) {
      setIsLoading(false);
    }
  }, [session.isSessionExpired]);

  const hasAttemptedInitialRefresh = React.useRef(false);

  useEffect(() => {
    // Only refresh token on first load if we have a refresh token but no user
    // Wait for session.refreshToken to be populated from localStorage

    if (
      session.refreshToken &&
      !user &&
      !session.isSessionExpired &&
      !hasAttemptedInitialRefresh.current
    ) {
      hasAttemptedInitialRefresh.current = true;
      refreshToken(true, session.refreshToken);
    }
  }, [session.refreshToken, user, session.isSessionExpired]); // Watch for session.refreshToken changes

  return {
    isSessionExpired: session.isSessionExpired,
    user,
    isLoading,
    getUserLoading,
    refreshTokenFunc: refreshToken,
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    setHandleServerError,
    storeSession,
    clearSession,
    updateUser,
  };
}

export function AuthProvider({ children, apolloClient }) {
  const auth = useProvideAuth(apolloClient);
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
